import React, { type ReactNode } from "react";
import { record } from "../lib/breadcrumbs.js";
import { abrirRelatoDeErro, ReportarErro } from "./ReportarErro.js";

// =====================================================================
// Rede de segurança para exceções de renderização.
//
// Sem isto, um erro dentro de um componente React desmonta a árvore
// INTEIRA e a pessoa fica olhando para uma página branca. Página branca é
// o pior estado possível de suporte: não há mensagem para relatar, não há
// botão para clicar, e o relato que chega é "o sistema sumiu" — sem stack,
// sem rota, sem nada.
//
// O boundary transforma isso numa tela que (a) explica, (b) preserva a
// trilha e (c) já traz o botão de relatar com o erro preenchido.
// =====================================================================

interface Props {
  children: ReactNode;
}
interface State {
  erro: Error | null;
  componente: string | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { erro: null, componente: null };

  static getDerivedStateFromError(erro: Error): Partial<State> {
    return { erro };
  }

  componentDidCatch(erro: Error, info: React.ErrorInfo): void {
    // A pilha de COMPONENTES (não a de JS) é o que aponta a tela culpada —
    // a pilha de JS em bundle de produção é uma sopa de nomes minificados.
    const componente = (info.componentStack ?? "").split("\n").slice(0, 6).join("\n").trim();
    this.setState({ componente });
    record("erro", `Tela quebrou: ${erro.message}`, {
      detail: [erro.stack, componente].filter(Boolean).join("\n---\n"),
      ok: false,
    });
  }

  render(): ReactNode {
    const { erro, componente } = this.state;
    if (!erro) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-50/60 p-6">
        <div className="card w-full max-w-lg overflow-hidden">
          <div className="brand-stripe h-1.5 w-full" />
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-3xl">
              ⚠
            </div>
            <h1 className="text-lg font-semibold text-slate-900">Esta tela parou de responder</h1>
            <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">
              O erro foi registrado com os seus últimos passos. Relatar leva alguns segundos e é o que faz
              alguém corrigir.
            </p>

            <p className="mx-auto mt-4 max-w-sm break-words rounded-lg bg-slate-50 px-3 py-2 text-left font-mono text-[11px] text-slate-600">
              {erro.message}
            </p>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <button
                className="btn-primary"
                data-testid="boundary-reportar"
                onClick={() =>
                  abrirRelatoDeErro({
                    title: `Tela parou de responder: ${erro.message}`.slice(0, 160),
                    description: [
                      "A tela ficou em branco e apareceu a mensagem de erro do portal.",
                      componente ? `\nComponente:\n${componente}` : "",
                    ].join(""),
                    // Uma tela que não renderiza é, por definição, bloqueio —
                    // não faz sentido pedir que a pessoa classifique isso.
                    severity: "bloqueia",
                  })
                }
              >
                🐞 Reportar este erro
              </button>
              <button className="btn-ghost" onClick={() => this.setState({ erro: null, componente: null })}>
                Tentar de novo
              </button>
              <a className="btn-ghost" href="/">
                Ir para o início
              </a>
            </div>
          </div>
        </div>

        {/* O formulário de relato vive no Layout — que acabou de ser
            desmontado junto com a árvore que quebrou. Sem esta instância, o
            botão acima dispararia um evento que ninguém está ouvindo: o
            mecanismo de socorro morre exatamente no acidente para o qual
            foi feito. `oculto` monta só o ouvinte, sem duplicar o botão. */}
        <ReportarErro oculto />
      </div>
    );
  }
}
